import {
  CoreInputModelUtil,
  getNewErrors,
  InputErrorModel,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import {
  BillingStartFromInterval,
  ItemBillingEnds,
  ItemBillingEndsAfterInterval,
  ItemBillingStartAfterInterval,
  ItemBillingType,
  ItemIncreasePriceBy,
  ItemIncreasePriceInterval,
  PricingModel,
  RecurringBillingInterval,
  RecurringFeeInterval,
  TierMode,
} from "../enums";
import {
  IAPICreateUpdatePriceModel,
  IAPICustomPricing,
  IAPIPackagePricing,
  IAPIPackagePricingOptions,
  IAPIPriceCancellationTerms,
  IAPIPriceTermSettings,
  IAPIRecurrencePriceSettings,
  IAPIStandardPricing,
  IAPITierPricing,
  IAPITierSettings,
} from "../interfaces";

export class PriceModel {
  id: string;
  pricingModel: PricingModel;
  name: string;
  billingType: ItemBillingType;
  termSettings?: PriceTermSettings | null;
  recurrencePriceSettings?: RecurrencePriceSettings | null;
  constructor(data?: Partial<PriceModel>) {
    this.id = (data && data.id) || "";
    this.pricingModel = (data && data.pricingModel) || PricingModel.Standard;
    this.name = (data && data.name) || "";
    this.billingType = (data && data.billingType) || ItemBillingType.Term;
    this.termSettings =
      (data && data.termSettings && new PriceTermSettings(data.termSettings)) ||
      null;
    this.recurrencePriceSettings =
      (data &&
        data.recurrencePriceSettings &&
        new RecurrencePriceSettings(data.recurrencePriceSettings)) ||
      null;
  }

  static deserializeAPIModel(data: IAPICreateUpdatePriceModel): PriceModel {
    const mappedData: Partial<PriceModel> = {
      id: data.id,
      name: data.name,
      billingType: data.billingType,
      termSettings:
        data.termSettings &&
        (data.billingType === ItemBillingType.Term ||
          data.billingType === ItemBillingType.TermPlusRecurring)
          ? PriceTermSettings.deserializeAPIModel(data.termSettings)
          : null,
      recurrencePriceSettings:
        data.recurrenceSettings &&
        (data.billingType === ItemBillingType.Recurring ||
          data.billingType === ItemBillingType.TermPlusRecurring)
          ? RecurrencePriceSettings.deserializeAPIModel(data.recurrenceSettings)
          : null,
    };
    return new PriceModel(mappedData);
  }
}
export class PriceTermSettings {
  intervalApplies: boolean;
  termInterval: RecurringBillingInterval;
  termIntervalCount: number;
  splitPayments: boolean;
  initialPayment: number;
  totalPayments: number;
  billingInterval: RecurringBillingInterval;
  billingIntervalCount: number;
  billingStartsAfterInterval: BillingStartFromInterval;
  pricingModel: PricingModel;
  standardPricing?: StandardPricingModel | null;
  packagePricing?: PackagePricingModel | null;
  customPricing?: CustomPricingModel | null;
  tierPricing?: TierPricingModel | null;
  volumePricing?: TierPricingModel | null;
  constructor(data?: Partial<PriceTermSettings>) {
    this.intervalApplies = data?.intervalApplies === true;
    this.termInterval =
      (data && data.termInterval) || RecurringBillingInterval.Month;
    this.termIntervalCount = (data && data.termIntervalCount) || 0;
    this.splitPayments = data?.splitPayments === true;
    this.initialPayment = (data && data.initialPayment) || 0;
    this.totalPayments = (data && data.totalPayments) || 0;
    this.billingInterval =
      (data && data.billingInterval) || RecurringBillingInterval.Month;
    this.billingIntervalCount = (data && data.billingIntervalCount) || 0;
    this.billingStartsAfterInterval =
      (data && data.billingStartsAfterInterval) ||
      BillingStartFromInterval.Immediately;
    this.pricingModel = (data && data.pricingModel) || PricingModel.Standard;

    this.standardPricing =
      data &&
      data.standardPricing &&
      new StandardPricingModel(data.standardPricing);
    this.packagePricing =
      data &&
      data.packagePricing &&
      new PackagePricingModel(data.packagePricing);
    this.customPricing =
      data && data.customPricing && new CustomPricingModel(data.customPricing);
    this.tierPricing =
      data && data.tierPricing && new TierPricingModel(data.tierPricing);
    this.volumePricing =
      data && data.volumePricing && new TierPricingModel(data.volumePricing);
  }

  createAPIModel(): IAPIPriceTermSettings {
    return {
      intervalApplies: this.intervalApplies,
      termInterval: this.termInterval,
      termIntervalCount: this.termIntervalCount,
      splitPayments: this.splitPayments,
      initialPayment: this.initialPayment,
      totalPayments: this.totalPayments,
      pricingModel: this.pricingModel,
      standardPricing: this.standardPricing
        ? this.standardPricing.createAPIModel()
        : null,
      packagePricing: this.packagePricing
        ? this.packagePricing.createAPIModel()
        : null,
      customPricing: this.customPricing
        ? this.customPricing.createAPIModel()
        : null,
      tierPricing: this.tierPricing ? this.tierPricing.createAPIModel() : null,
    };
  }

  static deserializeAPIModel(data: IAPIPriceTermSettings): PriceTermSettings {
    const mappedData: Partial<PriceTermSettings> = {
      intervalApplies: data.intervalApplies,
      termInterval: data.termInterval,
      termIntervalCount: data.termIntervalCount,
      splitPayments: data.splitPayments,
      initialPayment: data.initialPayment,
      totalPayments: data.totalPayments,
      pricingModel: data.pricingModel,
      standardPricing: data.standardPricing
        ? StandardPricingModel.desrializeAPI(data.standardPricing)
        : null,
      packagePricing: data.packagePricing
        ? PackagePricingModel.deserializeAPIModel(data.packagePricing)
        : null,
      customPricing: data.customPricing
        ? CustomPricingModel.desrializeAPI(data.customPricing)
        : null,
      volumePricing:
        data.pricingModel === PricingModel.Volume && data.tierPricing
          ? TierPricingModel.desrializeAPI(data.tierPricing)
          : null,
      tierPricing:
        data.pricingModel === PricingModel.Tiered &&
        data.tierPricing &&
        data.tierPricing
          ? TierPricingModel.desrializeAPI(data.tierPricing)
          : null,
    };
    return new PriceTermSettings(mappedData);
  }
}

export class RecurrencePriceSettings {
  lifeTime: boolean;
  termInterval: RecurringBillingInterval;
  termIntervalCount: number;
  splitPayments: boolean;
  initialPayment: number;
  totalPayments: number;
  billingStartIntervalCount: number;
  billingInterval: keyof typeof RecurringBillingInterval;
  billingIntervalCount: number;
  bllingStartAfterIntervalCount: number;
  billingStartInterval: keyof typeof BillingStartFromInterval;
  billingStartAfterInterval: keyof typeof ItemBillingStartAfterInterval;
  billingEndsAfterInterval: keyof typeof ItemBillingEndsAfterInterval;
  billingEndsAfterIntervalCount: number;
  billingEnds: keyof typeof ItemBillingEnds;
  billingEndIntervalCount: number;
  isRenewalFee: boolean;
  renewalFee: number;
  feeInterval: keyof typeof RecurringFeeInterval;
  isIncreasePrice: boolean;
  increasePriceInterval: keyof typeof ItemIncreasePriceInterval;
  increasePriceBy: keyof typeof ItemIncreasePriceBy;
  increasePriceValue: number;
  prorateFirstPayment: boolean;
  pricingModel: PricingModel;
  standardPricing?: StandardPricingModel | null;
  packagePricing?: PackagePricingModel | null;
  customPricing?: CustomPricingModel | null;
  tierPricing?: TierPricingModel | null;
  volumePricing?: TierPricingModel | null;
  constructor(data?: Partial<RecurrencePriceSettings>) {
    this.lifeTime = data?.lifeTime === true;
    this.termInterval =
      (data && data.termInterval) || RecurringBillingInterval.Month;
    this.termIntervalCount = (data && data.termIntervalCount) || 0;
    this.splitPayments = data?.splitPayments === true;
    this.initialPayment = (data && data.initialPayment) || 0;
    this.totalPayments = (data && data.totalPayments) || 0;
    this.billingStartIntervalCount =
      (data && data.billingStartIntervalCount) || 0;
    this.billingInterval =
      (data && data.billingInterval) || RecurringBillingInterval.Month;
    this.billingIntervalCount = (data && data.billingIntervalCount) || 0;

    this.bllingStartAfterIntervalCount =
      (data && data.bllingStartAfterIntervalCount) || 0;
    this.billingStartInterval =
      (data && data.billingStartInterval) ||
      BillingStartFromInterval.Immediately;
    this.billingEndsAfterInterval =
      (data && data.billingEndsAfterInterval) ||
      (Object.keys(ItemBillingEndsAfterInterval).find(
        (key) =>
          ItemBillingEndsAfterInterval[
            key as keyof typeof ItemBillingEndsAfterInterval
          ] === ItemBillingEndsAfterInterval.Payments,
      ) as keyof typeof ItemBillingEndsAfterInterval);
    this.billingStartAfterInterval =
      (data && data.billingStartAfterInterval) ||
      ItemBillingStartAfterInterval.Month;
    this.billingEndsAfterIntervalCount =
      (data && data.billingEndsAfterIntervalCount) || 0;
    this.billingEnds =
      (data && data.billingEnds) ||
      (Object.keys(ItemBillingEnds).find(
        (key) =>
          ItemBillingEnds[key as keyof typeof ItemBillingEnds] ===
          ItemBillingEnds.OnCancellation,
      ) as keyof typeof ItemBillingEnds);

    this.billingEndIntervalCount = (data && data.billingEndIntervalCount) || 0;
    this.isRenewalFee = data?.isRenewalFee === true;
    this.renewalFee = (data && data.renewalFee) || 0;
    this.feeInterval =
      (data && data.feeInterval) ||
      (Object.keys(RecurringFeeInterval).find(
        (key) =>
          RecurringFeeInterval[key as keyof typeof RecurringFeeInterval] ===
          RecurringFeeInterval.None,
      ) as keyof typeof RecurringFeeInterval);
    this.isIncreasePrice = data?.isIncreasePrice === true;
    this.increasePriceInterval =
      (data && data.increasePriceInterval) ||
      (Object.keys(ItemIncreasePriceInterval).find(
        (key) =>
          ItemIncreasePriceInterval[
            key as keyof typeof ItemIncreasePriceInterval
          ] === ItemIncreasePriceInterval.None,
      ) as keyof typeof ItemIncreasePriceInterval);
    this.increasePriceBy =
      (data && data.increasePriceBy) || ItemIncreasePriceBy.Percent;
    this.increasePriceValue = (data && data.increasePriceValue) || 0;
    this.prorateFirstPayment = data?.prorateFirstPayment === true;

    this.pricingModel = (data && data.pricingModel) || PricingModel.Standard;

    this.standardPricing =
      data &&
      data.standardPricing &&
      new StandardPricingModel(data.standardPricing);
    this.packagePricing =
      data &&
      data.packagePricing &&
      new PackagePricingModel(data.packagePricing);
    this.customPricing =
      data && data.customPricing && new CustomPricingModel(data.customPricing);
    this.tierPricing =
      data && data.tierPricing && new TierPricingModel(data.tierPricing);
  }

  createAPIModel(): IAPIRecurrencePriceSettings {
    return {
      termInterval: this.termInterval,
      termIntervalCount: this.termIntervalCount,
      splitPayments: this.splitPayments,
      initialPayment: this.initialPayment,
      totalPayments: this.totalPayments,
      billingInterval: this.billingInterval,
      billingIntervalCount: this.billingIntervalCount,
      billingStartAfterInterval: this.billingStartAfterInterval,
      billingStartIntervalCount: this.billingStartIntervalCount,
      billingStartInterval: this.billingStartInterval,
      billingEndsInterval: this.billingEnds,
      billingEndIntervalCount: this.billingEndIntervalCount,
      billingEndsAfterInterval: this.billingEndsAfterInterval,
      billingEndsAfterIntervalCount: this.billingEndsAfterIntervalCount,
      isIncreasePrice: this.isIncreasePrice,
      prorateFirstPayment: this.prorateFirstPayment,
      cancellationTerms: {
        daysNotice: 0,
        minimumDue: 0,
        cancellationFee: 0,
      },
      fee: this.renewalFee,
      feeInterval: this.feeInterval,
      increasePriceBy: this.increasePriceBy,
      increasePriceAmount:
        this.increasePriceBy === ItemIncreasePriceBy.Amount
          ? this.increasePriceValue
          : 0,
      increasePricePercentage:
        this.increasePriceBy === ItemIncreasePriceBy.Percent
          ? this.increasePriceValue
          : 0,
      increasePriceInterval: this.increasePriceInterval,
      pricingModel: this.pricingModel,
      standardPricing: this.standardPricing
        ? this.standardPricing.createAPIModel()
        : null,
      packagePricing: this.packagePricing
        ? this.packagePricing.createAPIModel()
        : null,
      customPricing: this.customPricing
        ? this.customPricing.createAPIModel()
        : null,
      tierPricing: this.tierPricing ? this.tierPricing.createAPIModel() : null,
    };
  }

  static deserializeAPIModel(
    data: IAPIRecurrencePriceSettings,
  ): RecurrencePriceSettings {
    const mappedData: Partial<RecurrencePriceSettings> = {
      splitPayments: data.splitPayments,
      prorateFirstPayment: data.prorateFirstPayment,
      initialPayment: data.initialPayment,
      totalPayments: data.totalPayments,
      billingInterval: data.billingInterval || "Month",
      billingIntervalCount: data.billingIntervalCount,
      billingStartAfterInterval: data.billingStartAfterInterval,
      billingStartIntervalCount: data.billingStartIntervalCount,
      billingStartInterval: data.billingStartInterval,
      billingEnds: data.billingEndsInterval,
      billingEndIntervalCount: data.billingEndIntervalCount,
      billingEndsAfterInterval: data.billingEndsAfterInterval,
      renewalFee: data.fee,
      feeInterval: data.feeInterval,
      increasePriceValue:
        data.increasePriceBy === ItemIncreasePriceBy.Amount
          ? data.increasePriceAmount
          : data.increasePricePercentage,
      increasePriceInterval: data.increasePriceInterval,
      increasePriceBy: data.increasePriceBy,
      pricingModel: data.pricingModel,
      standardPricing: data.standardPricing
        ? StandardPricingModel.desrializeAPI(data.standardPricing)
        : null,
      packagePricing: data.packagePricing
        ? PackagePricingModel.deserializeAPIModel(data.packagePricing)
        : null,
      customPricing: data.customPricing
        ? CustomPricingModel.desrializeAPI(data.customPricing)
        : null,
      volumePricing: data.tierPricing
        ? TierPricingModel.desrializeAPI(data.tierPricing)
        : null,
    };
    return new RecurrencePriceSettings(mappedData);
  }
}

export class PriceCancellationTerms {
  daysNotice: string;
  minimumDue: string;
  cancellationFee: string;
  errors: InputErrorModel<PriceCancellationTerms>[];
  constructor(data?: Partial<PriceCancellationTerms>) {
    this.daysNotice = (data && data.daysNotice) || "";
    this.minimumDue = (data && data.minimumDue) || "";
    this.cancellationFee = (data && data.cancellationFee) || "";
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<PriceCancellationTerms>(e);
        })) ||
      [];
  }

  createAPIModel(): IAPIPriceCancellationTerms {
    return {
      daysNotice: parseFloat(this.daysNotice || "0"),
      cancellationFee: parseFloat(this.cancellationFee || "0"),

      minimumDue: parseFloat(this.minimumDue || "0"),
    };
  }

  static deserializeAPIModel(
    data: IAPIPriceCancellationTerms,
  ): PriceCancellationTerms {
    const mappedData: Partial<PriceCancellationTerms> = {
      daysNotice: data.daysNotice > 0 ? data.daysNotice.toString() : "",
      minimumDue: UtilityMethods.fixNumber(data.minimumDue || 0),
      cancellationFee: UtilityMethods.fixNumber(data.cancellationFee || 0),
    };
    return new PriceCancellationTerms(mappedData);
  }

  hasError(field: keyof PriceCancellationTerms): boolean {
    return this.errors.some((e) => e.field === field);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<PriceCancellationTerms>[] = [];
    CoreInputModelUtil.validateDecimalValue(
      this.cancellationFee,
      "cancellationFee",
      newErrors,
    );
    CoreInputModelUtil.validateDecimalValue(
      this.minimumDue,
      "minimumDue",
      newErrors,
    );
    CoreInputModelUtil.validateDecimalValue(
      this.daysNotice,
      "daysNotice",
      newErrors,
    );
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }
}

export class CustomPricingModel {
  minimum: number;
  maximum: number;
  preset: number;
  cost: number;
  constructor(data?: Partial<CustomPricingModel>) {
    this.minimum = (data && data.minimum) || 0;
    this.maximum = (data && data.maximum) || 0;
    this.preset = (data && data.preset) || 0;
    this.cost = (data && data.cost) || 0;
  }

  createAPIModel(): IAPICustomPricing {
    return {
      minimum: this.minimum,
      maximum: this.maximum,
      preset: this.preset,
      cost: this.cost,
      setupFee: 0,
    };
  }
  static desrializeAPI(data: IAPICustomPricing): CustomPricingModel {
    const mappedData: Partial<CustomPricingModel> = {
      minimum: data.minimum,
      maximum: data.maximum,
      preset: data.preset,
      cost: data.cost,
    };
    return new CustomPricingModel(mappedData);
  }
}
export class StandardPricingModel {
  price: number;
  originalPrice: number;
  cost: number;
  setUpFee: number;
  showOriginalPrice: boolean;
  constructor(data?: Partial<StandardPricingModel>) {
    this.price = (data && data.price) || 0;
    this.originalPrice = (data && data.originalPrice) || 0;
    this.cost = (data && data.cost) || 0;
    this.setUpFee = (data && data.setUpFee) || 0;
    this.showOriginalPrice = data?.showOriginalPrice === true;
  }

  createAPIModel(): IAPIStandardPricing {
    return {
      price: this.price,
      originalPrice: this.originalPrice,
      cost: this.cost,
      setupFee: this.setUpFee,
      showOriginalPrice: this.showOriginalPrice,
    };
  }

  static desrializeAPI(data: IAPIStandardPricing): StandardPricingModel {
    const mappedData: Partial<StandardPricingModel> = {
      price: data.price,
      originalPrice: data.originalPrice,
      showOriginalPrice: data.showOriginalPrice,
      cost: data.cost,
      setUpFee: data.setupFee,
    };
    return new StandardPricingModel(mappedData);
  }
}

export class TierPricingModel {
  mode: TierMode;
  price: number;
  setUpFee: number;
  tiers: TierPriceSettings[];
  showOriginalPrice: boolean;
  originalPrice: number;
  constructor(data?: Partial<TierPricingModel>) {
    this.mode = (data && data.mode) || TierMode.Volume;
    this.tiers = (data && data.tiers) || [];
    this.price = data?.price || 0;
    this.setUpFee = (data && data.setUpFee) || 0;
    this.originalPrice = data?.originalPrice || 0;
    this.showOriginalPrice = data?.showOriginalPrice === true;
  }

  createAPIModel(): IAPITierPricing {
    return {
      mode: this.mode,
      price: this.price,
      showOriginalPrice: this.showOriginalPrice,
      originalPrice: this.originalPrice,
      setupFee: this.setUpFee,
      tiers: this.tiers.map((tier) => tier.createAPIModel()),
    };
  }
  static desrializeAPI(data: IAPITierPricing): TierPricingModel {
    const mappedData: Partial<TierPricingModel> = {
      price: data.price,
      showOriginalPrice: data.showOriginalPrice,
      originalPrice: data.originalPrice,
      setUpFee: data.setupFee,
      tiers:
        data.tiers?.map((t, index) => {
          const previous = data.tiers?.[index - 1];
          return TierPriceSettings.desrializeAPI(t, index, previous);
        }) || [],
    };
    return new TierPricingModel(mappedData);
  }
}
export class TierPriceSettings {
  discountPercentage: number;
  endValue: number;
  startValue: number;
  constructor(data?: Partial<TierPriceSettings>) {
    this.discountPercentage = (data && data.discountPercentage) || 0;
    this.endValue = (data && data.endValue) || 0;
    this.startValue = (data && data.startValue) || 0;
  }

  createAPIModel(): IAPITierSettings {
    return {
      discount: this.discountPercentage,
      upTo: this.endValue,
      startFrom: this.startValue,
    };
  }

  static desrializeAPI(
    data: IAPITierSettings,
    index: number,
    previous?: IAPITierSettings,
  ): TierPriceSettings {
    const mappedData: Partial<TierPriceSettings> = {
      discountPercentage: data.discount,
      endValue: data.upTo,
      startValue: data.startFrom, //index === 0 ? 1 : (previous?.upTo || 0) + 1,
    };
    return new TierPriceSettings(mappedData);
  }
}

export class PackagePricingModel {
  showOriginalPrice: boolean;
  showCost: boolean;
  options: PackagePricingOptions[];
  setupFee: number;
  constructor(data?: Partial<PackagePricingModel>) {
    this.showOriginalPrice = data?.showOriginalPrice === true;
    this.showCost = data?.showCost === true;
    this.options = (data && data.options) || [];
    this.setupFee = (data && data.setupFee) || 0;
  }

  createAPIModel(): IAPIPackagePricing {
    return {
      showOriginalPrice: this.showOriginalPrice,
      showCost: this.showCost,
      options: this.options.map((option) => option.createAPIModel()),
      setupFee: this.setupFee,
    };
  }

  static deserializeAPIModel(data: IAPIPackagePricing): PackagePricingModel {
    const mappedData: Partial<PackagePricingModel> = {
      showOriginalPrice: data.showOriginalPrice,
      showCost: data.showCost,
      options: data.options.map((option) =>
        PackagePricingOptions.deserializeAPIModel(option),
      ),
    };
    return new PackagePricingModel(mappedData);
  }
}

export class PackagePricingOptions {
  units: number;
  price: number;
  originalPrice: number;
  cost: number;
  setupFee: number;
  constructor(data?: Partial<PackagePricingOptions>) {
    this.units = (data && data.units) || 0;
    this.price = (data && data.price) || 0;
    this.originalPrice = (data && data.originalPrice) || 0;
    this.cost = (data && data.cost) || 0;
    this.setupFee = (data && data.setupFee) || 0;
  }

  createAPIModel(): IAPIPackagePricingOptions {
    return {
      units: this.units,
      price: this.price,
      originalPrice: this.originalPrice,
      cost: this.cost,
      setupFee: this.setupFee,
    };
  }

  static deserializeAPIModel(
    data: IAPIPackagePricingOptions,
  ): PackagePricingOptions {
    const mappedData: Partial<PackagePricingOptions> = {
      units: data.units,
      price: data.price,
      originalPrice: data.originalPrice,
      cost: data.cost,
    };
    return new PackagePricingOptions(mappedData);
  }
}
